import { visit } from 'unist-util-visit'
import type { Plugin } from 'unified'

/**
 * Plugin to remove all empty <p> tags throughout the document.
 *
 * In some markdown processing pipelines, empty paragraphs can be generated
 * which can cause layout issues. This plugin cleans those up by removing
 * all empty paragraph tags regardless of their parent element.
 */
export function removeEmptyPTags(): Plugin {
  return () => (tree: any) => {
    /**
     * Helper function to check if a node is an empty paragraph
     */
    const isEmptyParagraph = (node: any): boolean => {
      return (
        node.type === 'element' &&
        node.tagName === 'p' &&
        // Check for completely empty p tag
        (node.children.length === 0 ||
          // Or check for p tag with only whitespace text node
          (node.children.length === 1 &&
            node.children[0].type === 'text' &&
            !node.children[0].value.trim()))
      )
    }

    // Process all element nodes in the tree
    visit(tree, 'element', (node, index, parent) => {
      // Skip if there are no children or no parent/index (shouldn't happen, but just in case)
      if (!Array.isArray(node.children) || !parent || index === undefined) {
        return
      }

      // Remove all empty paragraphs from this node's children
      // const originalLength = node.children.length
      node.children = node.children.filter(
        (child: any) => !isEmptyParagraph(child),
      )

      // If we removed some children and this node is now empty, and this node is a paragraph,
      // it may now be empty itself and should be removed by a subsequent visit
    })
  }
}
